import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { userAppContext } from '@/context/Context';
import { PaidToolBadge } from '@/modules/featureBilling';
import { slugifyTrackingValue, trackEvent } from '@/utils/analytics';
import {
  getToolGroupLabel,
  toolShowsInfoLink,
  type NavToolMenuItem,
} from '@/components/NavMenus/navMenuConfig';
import { askAidaToolRecommendation } from './askAidaToolRecommendation';
import {
  clearStoredToolFinderRecommendation,
  readStoredToolFinderRecommendation,
  resolveToolFinderUserId,
  writeStoredToolFinderRecommendation,
} from './toolFinderStorage';
import {
  TOOL_FINDER_GOAL_OPTIONS,
  TOOL_FINDER_INTERFACE_OPTIONS,
  buildLocalRationale,
  buildToolFinderPrompt,
  parseToolFinderAnswer,
  rankToolsLocally,
  type ToolFinderAnswers,
  type ToolFinderGoal,
  type ToolFinderInterface,
} from './toolFinder';

type PanelStep = 'questions' | 'loading' | 'results';

type ToolFinderPanelProps = {
  open: boolean;
  onClose: () => void;
  tools: NavToolMenuItem[];
  renderIcon: (tool: NavToolMenuItem) => ReactNode;
};

function trackFinder(label: string, kind: string) {
  trackEvent({
    name: 'navigation_click',
    buttonId: `tools_finder_${kind}_${slugifyTrackingValue(label)}`,
    label: `${label} — ${kind}`,
    page: 'tools',
    section: 'tool_finder',
    feature: 'aida',
    eventCategory: 'navigation',
  });
}

function resolveToolsByIds(ids: string[], tools: NavToolMenuItem[]) {
  const byId = new Map(tools.map(t => [t.id, t]));
  return ids.map(id => byId.get(id)).filter(Boolean) as NavToolMenuItem[];
}

function emptyPanelState() {
  return {
    step: 'questions' as PanelStep,
    goal: null as ToolFinderGoal | null,
    interfacePref: null as ToolFinderInterface | null,
    recommended: [] as NavToolMenuItem[],
    summary: '',
    whyById: {} as Record<string, string>,
    source: 'aida' as 'aida' | 'local',
    error: null as string | null,
    aidaRawPreview: null as string | null,
  };
}

export default function ToolFinderPanel({ open, onClose, tools, renderIcon }: ToolFinderPanelProps) {
  const ctx = userAppContext() ?? {};
  const { isAuthenticated, user, authorization, groups } = ctx as {
    isAuthenticated?: boolean;
    user?: { sub?: string };
    authorization?: string;
    groups?: string[];
  };
  const userId = resolveToolFinderUserId(user?.sub);
  const isAcademicUser = Boolean(isAuthenticated && groups?.includes('ROLE_ACADEMIC'));
  const canUseAida = Boolean(isAuthenticated && authorization && !isAcademicUser);
  const panelRef = useRef<HTMLElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [step, setStep] = useState<PanelStep>('questions');
  const [goal, setGoal] = useState<ToolFinderGoal | null>(null);
  const [interfacePref, setInterfacePref] = useState<ToolFinderInterface | null>(null);
  const [recommended, setRecommended] = useState<NavToolMenuItem[]>([]);
  const [summary, setSummary] = useState('');
  const [whyById, setWhyById] = useState<Record<string, string>>({});
  const [source, setSource] = useState<'aida' | 'local'>('aida');
  const [error, setError] = useState<string | null>(null);
  const [aidaRawPreview, setAidaRawPreview] = useState<string | null>(null);

  const canSubmit = Boolean(goal && interfacePref);

  useEffect(() => {
    abortRef.current?.abort();
    abortRef.current = null;

    const stored = readStoredToolFinderRecommendation(userId);
    if (!stored) {
      const empty = emptyPanelState();
      setStep(empty.step);
      setGoal(empty.goal);
      setInterfacePref(empty.interfacePref);
      setRecommended(empty.recommended);
      setSummary(empty.summary);
      setWhyById(empty.whyById);
      setSource(empty.source);
      setError(empty.error);
      setAidaRawPreview(empty.aidaRawPreview);
      return;
    }

    setGoal(stored.answers.goal);
    setInterfacePref(stored.answers.interfacePref);
    setRecommended(resolveToolsByIds(stored.toolIds, tools));
    setSummary(stored.summary);
    setWhyById(stored.whyById ?? {});
    setSource(stored.source);
    setError(stored.error ?? null);
    setAidaRawPreview(stored.aidaRawPreview ?? null);
    setStep('results');
  }, [userId, tools]);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [open, step]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const persistRecommendation = ({
    answers,
    toolIds,
    summaryText,
    why,
    resultSource,
    rawPreview,
    errorText,
  }: {
    answers: ToolFinderAnswers;
    toolIds: string[];
    summaryText: string;
    why: Record<string, string>;
    resultSource: 'aida' | 'local';
    rawPreview?: string | null;
    errorText?: string | null;
  }) => {
    writeStoredToolFinderRecommendation(userId, {
      version: 1,
      savedAt: new Date().toISOString(),
      answers,
      toolIds,
      summary: summaryText,
      whyById: why,
      source: resultSource,
      aidaRawPreview: rawPreview ?? null,
      error: errorText ?? null,
    });
  };

  const resetToQuestions = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    clearStoredToolFinderRecommendation(userId);
    const empty = emptyPanelState();
    setStep(empty.step);
    setGoal(empty.goal);
    setInterfacePref(empty.interfacePref);
    setRecommended(empty.recommended);
    setSummary(empty.summary);
    setWhyById(empty.whyById);
    setSource(empty.source);
    setError(empty.error);
    setAidaRawPreview(empty.aidaRawPreview);
  };

  const handleClose = () => {
    abortRef.current?.abort();
    onClose();
  };

  const handleGetRecommendation = async () => {
    if (!goal || !interfacePref) return;

    const answers: ToolFinderAnswers = { goal, interfacePref };
    trackFinder(`${goal}_${interfacePref}`, 'submit');
    setStep('loading');
    setError(null);
    setAidaRawPreview(null);
    setRecommended([]);
    setSummary('');
    setWhyById({});

    if (!canUseAida) {
      const ranked = rankToolsLocally(answers, tools, 2);
      const picked = ranked.map(r => r.tool);
      const summaryText = buildLocalRationale(answers, ranked);
      const errorText = !isAuthenticated
        ? 'Sign in with a plan that includes AIDA for AI-powered recommendations. Here is a guided match for now.'
        : isAcademicUser
          ? 'AIDA is not included in the free academic plan. Here is a guided match for now.'
          : 'AIDA is unavailable for your account. Here is a guided match for now.';
      setRecommended(picked);
      setSummary(summaryText);
      setSource('local');
      setError(errorText);
      persistRecommendation({
        answers,
        toolIds: picked.map(t => t.id),
        summaryText,
        why: {},
        resultSource: 'local',
        errorText,
      });
      setStep('results');
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const prompt = buildToolFinderPrompt(answers, tools);
      const answer = await askAidaToolRecommendation({
        prompt,
        authorization,
        userSub: user?.sub,
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      setAidaRawPreview(answer);
      const parsed = parseToolFinderAnswer(answer, tools);

      if (parsed?.toolIds.length) {
        const picked = resolveToolsByIds(parsed.toolIds, tools);
        const summaryText = parsed.summary || answer.slice(0, 320);
        setRecommended(picked);
        setSummary(summaryText);
        setWhyById(parsed.whyById);
        setSource('aida');
        setError(null);
        persistRecommendation({
          answers,
          toolIds: picked.map(t => t.id),
          summaryText,
          why: parsed.whyById,
          resultSource: 'aida',
          rawPreview: answer,
        });
      } else {
        const summaryText = answer.slice(0, 420);
        const errorText = 'AIDA replied, but we could not map the answer to catalog tools. Try asking again.';
        setRecommended([]);
        setSummary(summaryText);
        setWhyById({});
        setSource('aida');
        setError(errorText);
        persistRecommendation({
          answers,
          toolIds: [],
          summaryText,
          why: {},
          resultSource: 'aida',
          rawPreview: answer,
          errorText,
        });
      }
      setStep('results');
    } catch (err) {
      if (controller.signal.aborted) return;
      const ranked = rankToolsLocally(answers, tools, 2);
      const picked = ranked.map(r => r.tool);
      const summaryText = buildLocalRationale(answers, ranked);
      const errorText =
        err instanceof Error
          ? `${err.message} Showing a guided match instead.`
          : 'AIDA is unavailable right now. Showing a guided match instead.';
      setRecommended(picked);
      setSummary(summaryText);
      setWhyById({});
      setSource('local');
      setError(errorText);
      persistRecommendation({
        answers,
        toolIds: picked.map(t => t.id),
        summaryText,
        why: {},
        resultSource: 'local',
        errorText,
      });
      setStep('results');
    }
  };

  if (!open) return null;

  return (
    <section ref={panelRef} className="tools-finder" aria-labelledby="tools-finder-title" aria-live="polite">
      <header className="tools-finder__header">
        <div>
          <p className="tools-finder__eyebrow">Tool finder</p>
          <h2 id="tools-finder-title" className="tools-finder__title">
            Find the right tool for your research
          </h2>
        </div>
        <button
          type="button"
          className="tools-finder__close"
          onClick={handleClose}
          aria-label="Close tool finder"
        >
          <XMarkIcon aria-hidden />
        </button>
      </header>

      {step === 'questions' && (
        <div className="tools-finder__body">
          <p className="tools-finder__intro">
            {canUseAida
              ? 'Answer two quick questions — we will ask AIDA and show the tools it recommends.'
              : 'Answer two quick questions and we will suggest the best DISGENET tools for your research.'}
          </p>
          <fieldset className="tools-finder__fieldset">
            <legend className="tools-finder__legend">1. What do you want to do?</legend>
            <div className="tools-finder__options">
              {TOOL_FINDER_GOAL_OPTIONS.map(option => (
                <button
                  key={option.id}
                  type="button"
                  className={`tools-finder__option${goal === option.id ? ' is-active' : ''}`}
                  onClick={() => setGoal(option.id)}
                  aria-pressed={goal === option.id}
                >
                  <span className="tools-finder__option-label">{option.label}</span>
                  <span className="tools-finder__option-hint">{option.hint}</span>
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="tools-finder__fieldset">
            <legend className="tools-finder__legend">2. How do you prefer to work?</legend>
            <div className="tools-finder__options tools-finder__options--3">
              {TOOL_FINDER_INTERFACE_OPTIONS.map(option => (
                <button
                  key={option.id}
                  type="button"
                  className={`tools-finder__option${interfacePref === option.id ? ' is-active' : ''}`}
                  onClick={() => setInterfacePref(option.id)}
                  aria-pressed={interfacePref === option.id}
                >
                  <span className="tools-finder__option-label">{option.label}</span>
                  <span className="tools-finder__option-hint">{option.hint}</span>
                </button>
              ))}
            </div>
          </fieldset>

          <div className="tools-finder__actions">
            <button
              type="button"
              className="tools-finder__primary"
              disabled={!canSubmit}
              onClick={handleGetRecommendation}
            >
              {canUseAida ? 'Ask AIDA' : 'Get recommendation'}
              <ArrowRightIcon aria-hidden />
            </button>
          </div>
        </div>
      )}

      {step === 'loading' && (
        <div className="tools-finder__loading">
          <ArrowPathIcon className="tools-finder__spinner" aria-hidden />
          <p>{canUseAida ? 'Asking AIDA for the best match…' : 'Finding the best match…'}</p>
        </div>
      )}

      {step === 'results' && (
        <div className="tools-finder__body">
          <p className="tools-finder__result-note">
            {source === 'aida' ? 'Recommended by AIDA' : 'Guided match'}
          </p>
          {summary && <p className="tools-finder__summary">{summary}</p>}
          {error && <p className="tools-finder__error">{error}</p>}

          <div className="tools-finder__results">
            {recommended.map(tool => {
              const showInfo = toolShowsInfoLink(tool);
              const showTool = tool.showTool !== false;
              return (
                <article key={tool.id} className="tools-finder__card">
                  <div className="tools-finder__card-top">
                    <span className="tools-page__card-icon" aria-hidden>
                      {renderIcon(tool)}
                    </span>
                    <span className="tools-page__card-group">{getToolGroupLabel(tool.group)}</span>
                  </div>
                  <h3 className="tools-page__card-title">
                    <span>{tool.label}</span>
                    {tool.isPaidTool && <PaidToolBadge />}
                  </h3>
                  <p className="tools-page__card-desc">{whyById[tool.id] || tool.description}</p>
                  <div className="tools-page__card-footer">
                    <span className="tools-page__card-tag">{tool.tag}</span>
                    <div className="tools-page__card-actions">
                      {showInfo && (
                        <Link
                          to={tool.infoHref}
                          className="tools-page__card-action tools-page__card-action--muted"
                        >
                          Info
                        </Link>
                      )}
                      {showTool &&
                        (tool.toolExternal ? (
                          <a
                            href={tool.toolHref}
                            className="tools-page__card-action tools-page__card-action--primary"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open tool
                            <ArrowTopRightOnSquareIcon aria-hidden />
                          </a>
                        ) : (
                          <Link
                            to={tool.toolHref}
                            className="tools-page__card-action tools-page__card-action--primary"
                          >
                            Open tool
                            <ArrowRightIcon aria-hidden />
                          </Link>
                        ))}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {recommended.length === 0 && source === 'aida' && aidaRawPreview && (
            <details className="tools-finder__raw">
              <summary>See AIDA full reply</summary>
              <pre>{aidaRawPreview}</pre>
            </details>
          )}

          <div className="tools-finder__actions">
            <button type="button" className="tools-finder__secondary" onClick={resetToQuestions}>
              Ask again
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
